import { DateFilterTab, VisualData } from 'common/models/date-filter.model';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  format,
  formatDate,
  parseISO,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { useCallback, useEffect, useRef } from 'react';
import {
  DASHBOARD_PAGE,
  DATE_DROPDOWN_UNITS,
  DATE_FILTER_INITIAL_VALUE,
  DATE_FORMAT,
  DEFAULT_END_DATE,
  QUARTER_YEAR_FORMAT,
} from '../../common/constants';
import { AppPage, MenuItem } from 'common/models/page-config.models';
import { UnionBuffer } from '../models/file.model';

/**
 * Generates initials from a given name string.
 *
 * This function takes a full name as input and returns the initials
 * of the first and second words in the name. If the name contains
 * only one word, it returns the first letter of that word.
 *
 * @param name - The full name from which to generate initials.
 * @returns The initials generated from the name.
 */
export function generateAvatarInitials(name: string): string {
  if (!name) {
    return '';
  }

  const nameArray = name.split(' ');

  const firstInitial = nameArray[0] ? nameArray[0][0] : '';
  const secondInitial = nameArray[1] ? nameArray[1][0] : '';

  return firstInitial + secondInitial;
}

export function isReportViewMode(path?: string) {
  if (!path) {
    path = window.location.href;
  }
  return path.includes('reports/view');
}

/**
 * Determines if the given path corresponds to a report view or edit mode.
 *
 * @param path - The path to check.
 * @returns `true` if the path includes 'reports/view' or 'reports/edit', otherwise `false`.
 */
export function isReportEditOrViewMode(path: string) {
  return path.includes('reports/view') || path.includes('reports/editor');
}

export type NumberFormat =
  | 'rounded'
  | 'ceiled'
  | 'floored'
  | 'toFixedOne'
  | 'toFixedTwo'
  | 'absolute';

export function formatNumber(value: number, format: NumberFormat = 'absolute'): number {
  switch (format) {
    case 'rounded':
      return Math.round(value);
    case 'ceiled':
      return Math.ceil(value);
    case 'floored':
      return Math.floor(value);
    case 'toFixedOne':
      return parseFloat(value.toFixed(1));
    case 'toFixedTwo':
      return parseFloat(value.toFixed(2));
    case 'absolute':
    default:
      return value;
  }
}

export function percentage(
  partialValue: number,
  totalValue: number,
  numberFormat: NumberFormat = 'absolute'
): number {
  if (partialValue < 0) {
    return 0;
  }

  const result = (100 * partialValue) / totalValue;

  return formatNumber(result, numberFormat);
}

export function roundPercentage(partialValue: number, totalValue: number) {
  if (!partialValue) {
    return 0;
  }
  const result = Math.round((100 * partialValue) / totalValue);
  if (result < 1) {
    return 1;
  }
  return result;
}

export function absPercentage(partialValue: number, totalValue: number) {
  const result = (100 * partialValue) / totalValue;
  return result.toFixed(2);
}

export function valueFromPercentage(percentage: number, totalValue: number) {
  return Math.ceil((percentage * totalValue) / 100);
}

export function isDashboardPage(pageTitle: string | undefined) {
  return pageTitle?.includes(DASHBOARD_PAGE);
}

export function searchInObject(
  item: Record<string, any>,
  searchTerm: string,
  allowed: string[] = []
) {
  let match = false;
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  allowed.forEach((property) => {
    const isNull = item[property] == null;
    if (isNull) return;

    const matchString = (word: any) => {
      const asString = word.toString().toLowerCase();
      if (asString.indexOf(lowerCaseSearchTerm) >= 0) {
        match = true;
      }
    };

    if (typeof item[property] === 'object' && item[property] !== null) {
      Object.values(item[property]).forEach((word) => {
        matchString(word);
      });
    } else {
      matchString(item[property]);
    }
    if (match === true) {
      return;
    }

    const formattedDate = formatAppDate(item[property], DATE_FORMAT);
    match = formattedDate.toLowerCase().includes(lowerCaseSearchTerm);
  });
  return match;
}

export const generateHMAC = async (userEmail: string, secretKey: string) => {
  try {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(secretKey),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    // Sign the email id with the HMAC key
    const signature = await crypto.subtle.sign(
      'HMAC',
      cryptoKey,
      new TextEncoder().encode(userEmail)
    );

    // Convert the signature to a hex string
    const signatureArray = new Uint8Array(signature);
    const signatureHex = Array.from(signatureArray, (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('');

    return signatureHex;
  } catch (error) {
    console.error(error);
  }
};

export default function useTimeout(callback: any, delay: number) {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  const set = useCallback(() => {
    timeoutRef.current = setTimeout((): void => {
      callbackRef.current();
    }, delay);
  }, [delay]);
  const clear = useCallback(() => {
    timeoutRef.current && clearTimeout(timeoutRef.current);
  }, []);
  useEffect(() => {
    set();
    return clear;
  }, [delay, set, clear]);
  const reset = useCallback(() => {
    clear();
    set();
  }, [clear, set]);
  return { reset, clear };
}

export function get(object: any, path: any): any {
  const props = path.split(/[./[\]]/g).filter((p: string) => p);
  const prop = props.pop();
  // eslint-disable-next-line
  const parent = props.reduce((result: any, key: any): any => result[key], object);
  return parent?.[prop] ? parent[prop] : '';
}

export function isLocalhost() {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return true;
  }
  return false;
}

export function onlyUnique(value: any, index: number, array: any[]) {
  return array.indexOf(value) === index;
}

/**
 * Converts a given Date object to a Kwello date string format. Kwello dates take the actuald Date value, and Zero it WITHOUT converting to ISO.
 * ISO Conversion causes issues when selecting dates across timezones.
 *
 * @param date - The Date object to be converted.
 * @returns A string representing the date in Kwello format (yyyy-MM-ddTHH:mm:ss.sssZ).
 */
export const convertToKwelloDate = (date: Date): string => {
  return `${formatDate(date, 'yyyy-MM-dd')}T00:00:00.000`;
};

/**
 * Formats a date string based on the provided display date type and date values.
 *
 * @param {Object} params - The parameters for formatting the date string.
 * @param {DateFilterTab} params.displayDateType - The type of date display (e.g., LAST, SINCE, BETWEEN).
 * @param {Date} params.startDate - The start date for the date range.
 * @param {Date} params.endDate - The end date for the date range.
 * @param {Array} params.visualData - An array containing visual data for the LAST date type.
 * @returns {string} The formatted date string.
 */

interface FormatFilterDateStringProps {
  displayDateType: DateFilterTab;
  startDate: string;
  endDate: string;
  visualData: VisualData;
}

export function formatFilterDateString({
  displayDateType,
  startDate,
  endDate,
  visualData,
}: FormatFilterDateStringProps): string {
  switch (displayDateType) {
    case DateFilterTab.LAST:
      return visualData ? `Last ${visualData[0]} ${visualData[1]}` : '';
    case DateFilterTab.SINCE:
      return `Since ${formatAppDate(startDate, DATE_FORMAT)}`;
    case DateFilterTab.BETWEEN:
      return `Between ${formatAppDate(startDate, DATE_FORMAT)} - ${formatAppDate(endDate, DATE_FORMAT)}`;
    case DateFilterTab.NEXT:
      return visualData ? `Next ${visualData[0]} ${visualData[1]}` : '';
    default:
      return '';
  }
}

export function formatAppDate(date: string | Date | null, date_format = DATE_FORMAT) {
  // Let this check in place otherwise it crashes the whole application if there is no date supplied
  if (!date) {
    return '';
  }
  const convertedDate = new Date(date);

  if (isNaN(convertedDate.getTime())) {
    return date?.toString();
  }

  if (!(typeof date === 'string' || date instanceof Date)) {
    throw new Error('formatAppDate - Invalid date format');
  }

  try {
    if (date_format === QUARTER_YEAR_FORMAT) {
      const year = convertedDate.getFullYear();
      const month = convertedDate.getMonth(); // 0–11
      const quarter = Math.floor(month / 3) + 1;

      return `Q${quarter} - ${year}`;
    }

    const parsed = parseISO(date.toString(), {});
    return format(parsed, date_format);
  } catch (e) {
    // If date is NOT ISO format, then format the raw date.
    return format(date, date_format);
  }
}

/**
 * Calculates the start and end dates based on the given period.
 *
 * @param since - An array where the first element is the number of units and the second element is the unit type (days, weeks, months, or years).
 * @returns An object containing the start and end dates as strings, or undefined if the unit type is not recognized.
 */
export const caulculateDatesFromLastPeriod = (
  since: VisualData
): { start: string; end: string } | undefined => {
  const today = new Date();

  if (since[1] === DATE_DROPDOWN_UNITS.Days) {
    return {
      start: convertToKwelloDate(subDays(today, since[0])),
      end: DEFAULT_END_DATE,
    };
  }
  if (since[1] === DATE_DROPDOWN_UNITS.Weeks) {
    return {
      start: convertToKwelloDate(subWeeks(today, since[0])),
      end: DEFAULT_END_DATE,
    };
  }
  if (since[1] === DATE_DROPDOWN_UNITS.Months) {
    return {
      start: convertToKwelloDate(subMonths(today, since[0])),
      end: DEFAULT_END_DATE,
    };
  }
  if (since[1] === DATE_DROPDOWN_UNITS.Years) {
    return {
      start: convertToKwelloDate(subYears(today, since[0])),
      end: DEFAULT_END_DATE,
    };
  }

  return undefined;
};

export const calculateDatesFromNextPeriod = (
  next: VisualData
): { start: string; end: string } | undefined => {
  const today = new Date();

  if (next[1] === DATE_DROPDOWN_UNITS.Days) {
    return {
      start: convertToKwelloDate(addDays(today, next[0])),
      end: DEFAULT_END_DATE,
    };
  }
  if (next[1] === DATE_DROPDOWN_UNITS.Weeks) {
    return {
      start: convertToKwelloDate(addWeeks(today, next[0])),
      end: DEFAULT_END_DATE,
    };
  }
  if (next[1] === DATE_DROPDOWN_UNITS.Months) {
    return {
      start: convertToKwelloDate(addMonths(today, next[0])),
      end: DEFAULT_END_DATE,
    };
  }
  if (next[1] === DATE_DROPDOWN_UNITS.Years) {
    return {
      start: convertToKwelloDate(addYears(today, next[0])),
      end: DEFAULT_END_DATE,
    };
  }
};

export interface AdjustFilterDatesArgs {
  startDate: string;
  endDate: string;
  visualData: VisualData;
  visualType: DateFilterTab;
}

export const adjustFilterDates = ({
  startDate = DATE_FILTER_INITIAL_VALUE.tempStartDate,
  endDate = DATE_FILTER_INITIAL_VALUE.endDate,
  visualData = DATE_FILTER_INITIAL_VALUE.visualData,
  visualType = DATE_FILTER_INITIAL_VALUE.visualType,
}: AdjustFilterDatesArgs): { startDate: string; endDate: string } => {
  const calculated = caulculateDatesFromLastPeriod(visualData);

  switch (visualType) {
    case DateFilterTab.LAST:
      return {
        startDate: calculated?.start ?? startDate,
        endDate: DEFAULT_END_DATE,
      };
    case DateFilterTab.SINCE:
      return {
        startDate: startDate,
        endDate: DEFAULT_END_DATE,
      };
    case DateFilterTab.BETWEEN:
      return {
        startDate,
        endDate,
      };
    default:
      return {
        startDate,
        endDate,
      };
  }
};

/**
 * Truncates a string if it is longer than the specified length.
 *
 * @param value - The string to truncate.
 * @param length - The maximum length of the string before truncation. Default is 30.
 * @returns The truncated string with '...' appended if it was longer than the specified length.
 */
export function truncateString(value: string, length: number = 30): string {
  return value.length > length ? `${value.substring(0, length)}...` : value;
}

export function sanatizeString(value: string): string {
  return value.replace(/[^a-zA-Z0-9#: ]/g, '');
}

/**
 * Creates a key from a given string.
 *
 * This function takes a string as input and returns a new string
 * that is all lower case and has any spaces replaced with '-'.
 *
 * @param value - The string to convert into a key.
 * @returns The generated key string.
 */
export function createKeyFromString(value: string): string {
  return value.toLowerCase().replace(/\s+/g, '-');
}

/**
 * Builds a hierarchical tree structure of menu items from a flat array of pages.
 *
 * This function takes an array of AppPage objects and optionally a projectId, and converts them
 * into a nested tree structure of MenuItem objects. The tree is built by identifying parent-child
 * relationships between pages using their PageID and ParentPageID properties.
 *
 * @param pages - An array of AppPage objects representing the flat page structure
 * @param projectId - Optional project ID to associate with all menu items in the tree
 * @returns An array of top-level MenuItem objects, each potentially containing nested children
 *
 * The resulting tree structure:
 * - Pages without ParentPageID become top-level items
 * - Child pages are nested under their parent pages in the children array
 * - All levels are sorted by PageSortOrder
 * - Each MenuItem contains metadata like route, icon, display text etc from the original AppPage
 */
export function buildTree(pages: AppPage[], projectId?: string): MenuItem[] {
  // Recursive function to build the tree
  const buildChildren = (parent: AppPage, allPages: AppPage[], parentUrl: string): MenuItem => {
    const children = allPages
      .filter((child) => child.ParentPageID === parent.PageID)
      .sort((a, b) => a.PageSortOrder - b.PageSortOrder); // Sort children by pageSortOrder

    return {
      projectId: projectId,
      sortOrder: parent.PageSortOrder,
      id: parent.PageID.toString(),
      tag: parent.Tag,
      text: parent.PageDisplayName,
      icon: parent.PageIconName,
      route: parent.Route,
      isMenuBar: parent.isMenuBar,
      isTabBar: parent.isTabBar,
      powerBIPageName: parent.PowerBIPageName,
      powerBIReportID: parent.PowerBIReportID,
      children: children.map((child) =>
        buildChildren(child, allPages, `${parentUrl}/${parent.PageDisplayName.toLowerCase()}`)
      ),
    };
  };

  const pageTree = pages
    .filter((page) => !page.ParentPageID)
    .sort((a, b) => a.PageSortOrder - b.PageSortOrder) // Sort top-level pages
    .map((topPage) => buildChildren(topPage, pages, ''));

  // Start building the tree with top-level pages, sorted by pageSortOrder
  return pageTree;
}

export function useDebounce(callback: any, delay: number) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    (...args: any) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

export function getInitials(name: string): string {
  return name
    .split(/\s+/) // Split by whitespace
    .map((word) => word.charAt(0).toUpperCase()) // Get first letter and uppercase it
    .join(''); // Combine them
}

/**
 * Save any value to sessionStorage.
 * - Strings, numbers, booleans are stored directly.
 * - Objects/arrays are JSON stringified.
 */
export function setSessionItem<T>(key: string, value: T): void {
  try {
    const isPrimitive = (val: unknown) =>
      typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean';

    const storedValue = isPrimitive(value) ? String(value) : JSON.stringify(value);
    sessionStorage.setItem(key, storedValue);
  } catch (error) {
    console.error(`setSessionItem error for key "${key}":`, error);
  }
}

/**
 * Get a value from sessionStorage with auto JSON parse if needed.
 * @returns The stored value typed as T, or defaultValue if not found or error.
 */
export function getSessionItem<T>(key: string, defaultValue: T | null = null): T | null {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) return defaultValue;

    const trimmed = item.trim();
    const isJsonLike =
      trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"');

    return isJsonLike ? (JSON.parse(trimmed) as T) : (item as unknown as T);
  } catch (error) {
    console.error(`getSessionItem error for key "${key}":`, error);
    return defaultValue;
  }
}

/**
 * Remove a key from sessionStorage.
 */
export function removeSessionItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`removeSessionItem error for key "${key}":`, error);
  }
}

export function shallowEqual(obj1: any, obj2: any, keys: string[]) {
  return keys.every((key) => obj1[key] === obj2[key]);
}

export function castToType<T>(value: any) {
  return value as unknown as T;
}

export function bufferToFile(
  bytes: UnionBuffer,
  name: string,
  type = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
): File {
  const parts = bytes instanceof ArrayBuffer ? [bytes] : [bytes as Uint8Array];
  const file = new File(castToType<BlobPart[]>(parts), name, { type, lastModified: Date.now() });
  return file;
}

/**
 * Sanitizes a file name by removing underscores and the prefix before the first underscore.
 *
 * @param fileName - The file name to sanitize.
 * @returns The sanitized file name.
 */
export function sanitizeFileName(fileName: string): string {
  const withoutPrefix = fileName.substring(fileName.indexOf('_') + 1);
  const withoutUnderscores = withoutPrefix.replace(/_/g, ' ');
  const withoutHyphens = withoutUnderscores.replace(/-/g, ' ');
  return withoutHyphens;
}
